import {
  ChangeDetectionStrategy,
  Component,
  OnDestroy,
  OnInit,
  Signal
} from "@angular/core";
import { AbstractControl, Validators } from "@angular/forms";
import { ActivatedRoute } from "@angular/router";
import { Store } from "@ngrx/store";
import { debounceTime, filter } from "rxjs";

import * as BookingState from "src/app/state";
import { AppError } from "src/app/state";

import { FormService } from "src/app/services/form.service";
import { PagingService } from "src/app/services/paging.service";
import { IconService } from "src/app/services/icon.service";
import { FeatureAppService } from "src/app/services/feature-app.service";
import { BusinessProposalService } from "src/app/services/business-proposal.service";

import {
  GetTechUpdateStatusResponse,
  VehicleSearchResponse
} from "src/domain/client";
import {
  ComponentStateEnum,
  PageBaseComponent
} from "../../base/page-base/page-base.component";
import { LowerCasePipe, UpperCasePipe } from "@angular/common";
import { LoadingComponent } from "../../layout/loading/loading.component";
import { PageContainerComponent } from "../../layout/page-container/page-container.component";
import { RegistrationNumberComponent } from "../../inputs/registration-number/registration-number.component";
import { TextBoxComponent } from "../../inputs/text-box/text-box.component";
import { IconComponent } from "../../layout/icon/icon.component";
import { InvalidInfoBoxComponent } from "../../layout/invalid-info-box/invalid-info-box.component";
import { MatProgressSpinner } from "@angular/material/progress-spinner";
import { SliderComponent } from "../../inputs/slider/slider.component";
import { InfoCardComponent } from "../../layout/info-card/info-card.component";
import { FirstToUpperPipe } from "src/app/pipes/sharedpipes/first-to-upper.pipe";
import { TranslateModule } from "@ngx-translate/core";
import {
  IsFeatureAppDirective,
  IsNotFeatureAppDirective
} from "src/app/directives/is-feature-app.directive";
import { CarBrandPipe } from "src/app/pipes/car-brand.pipe";
import { UrlService } from "src/app/services/url.service";
import { OutputTransformFn } from "ngx-mask";

@Component({
  selector: "app-about-vehicle",
  templateUrl: "./about-vehicle.component.html",
  imports: [
    LoadingComponent,
    PageContainerComponent,
    RegistrationNumberComponent,
    TextBoxComponent,
    IconComponent,
    InvalidInfoBoxComponent,
    MatProgressSpinner,
    SliderComponent,
    InfoCardComponent,
    UpperCasePipe,
    LowerCasePipe,
    FirstToUpperPipe,
    TranslateModule,
    IsNotFeatureAppDirective,
    IsFeatureAppDirective
  ],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class AboutVehicleComponent
  extends PageBaseComponent
  implements OnInit, OnDestroy
{
  readonly isLoading: Signal<boolean>;
  readonly error: Signal<AppError | null>;
  readonly carInfo: Signal<VehicleSearchResponse | null>;
  readonly techUpdateStatus: Signal<GetTechUpdateStatusResponse | null>;

  businessProposalStatus: ComponentStateEnum;
  businessProposalStatusText: string;

  constructor(
    public formService: FormService, // Still needed for the FormGroup instance during transition.
    private route: ActivatedRoute,
    public pagingService: PagingService,
    public iconService: IconService,
    private featureAppService: FeatureAppService,
    private carBrandPipe: CarBrandPipe,
    private businessProposalService: BusinessProposalService,
    private store: Store,
    public urlService: UrlService
  ) {
    super();
    this.isLoading = this.store.selectSignal(BookingState.selectIsLoading);
    this.error = this.store.selectSignal(BookingState.selectError);
    this.carInfo = this.store.selectSignal(BookingState.selectCarInfo);
    this.techUpdateStatus = this.store.selectSignal(
      BookingState.selectTechUpdateStatus
    );

    this.formService.aboutVehicleForm.markAsPristine();
    this.formService.previousFormValue =
      this.formService.aboutVehicleForm.value;

    if (this.formService.aboutVehicleForm.valid) return;

    if (this.featureAppService.featureAppRegNr()) {
      this.formService.aboutVehicleForm.patchValue({
        carText: this.featureAppService.featureAppRegNr()
      });
    }

    this.subscription.add(
      this.route.queryParamMap.subscribe({
        next: (result: any) => {
          const carText = this.carText.value
            ? this.carText.value
            : this.formService.searchedByRegno
              ? result.params.regnr
                ? result.params.regnr.replace(" ", "")
                : result.params.rn
                  ? result.params.rn.replace(" ", "")
                  : ""
              : result.params.chassi
                ? result.params.chassi.replace(" ", "")
                : result.params.vin
                  ? result.params.vin.replace(" ", "")
                  : "";

          const mileage = this.milage.value
            ? this.milage.value
            : result.params.milage
              ? parseInt(result.params.milage, 10)
                ? parseInt(result.params.milage, 10)
                : null
              : result.params.mileage
                ? parseInt(result.params.mileage, 10)
                  ? parseInt(result.params.mileage, 10)
                  : null
                : null;

          this.formService.aboutVehicleForm.patchValue({
            carText,
            milage: mileage
          });

          this.formService.providerIds = result.params.providers
            ? decodeURIComponent(result.params.providers).split(",")
            : [];
          this.formService.providerIds = result.params.dealers
            ? decodeURIComponent(result.params.dealers).split(",")
            : this.formService.providerIds;
          this.formService.providerIds = result.params.dl
            ? decodeURIComponent(result.params.dl).split(",")
            : this.formService.providerIds;
          if (this.formService.providerIds.length)
            this.formService.providerIds = this.formService.providerIds;

          this.store.dispatch(
            BookingState.updateProviderIds({
              providerIds: this.formService.providerIds
            })
          );
        }
      })
    );
  }

  ngOnInit(): void {
    this._insertReadPeakScript();

    if (this.businessProposalService.isBusinessProposalActive) {
      this.businessProposalService.getStatus().subscribe({
        next: (status) => {
          this.businessProposalStatus = status;
          this.businessProposalStatusText =
            this.businessProposalService.getStatusReasonUntranslated();
          if (
            ![
              ComponentStateEnum.Loading,
              ComponentStateEnum.Initialized
            ].includes(status)
          ) {
            this.initForm();
          }
        }
      });
    } else {
      this.initForm();
    }
  }

  initForm(): void {
    this.carText.setValidators([
      Validators.required,
      Validators.pattern(
        this.formService.searchedByRegno
          ? this.formService.regNrRegex
          : this.formService.chassiNrRegex
      )
    ]);

    if (this.carText.valid) {
      this.onSearchConfirm();
    }
    this.setSubscriptions();
  }

  onSearchConfirm(): void {
    const form = this.formService.aboutVehicleForm;
    if (!form.controls.carText.valid) {
      return;
    }
    this.store.dispatch(
      BookingState.submitAboutVehicle({
        chassiNumber: form.value.carText.replace(/\s/g, ""),
        odometer: form.value.milage,
        searchedByRegno: this.formService.searchedByRegno
      })
    );
  }

  onTextTypeToggle(): void {
    this.formService.searchedByRegno = !this.formService.searchedByRegno;
    this.onCarChange();
  }

  onCarChange(): void {
    this.store.dispatch(BookingState.clearCarInfo());
    this.formService.aboutVehicleForm.markAsDirty();
    this.formService.aboutVehicleForm.patchValue({
      carText: ""
    });
    this.formService.aboutVehicleForm.markAsUntouched();
    this.carText.setValidators([
      Validators.required,
      Validators.pattern(
        this.formService.searchedByRegno
          ? this.formService.regNrRegex
          : this.formService.chassiNrRegex
      )
    ]);
  }

  setSubscriptions(): void {
    this.subscription.add(
      this.carText.valueChanges
        .pipe(
          debounceTime(300),
          filter(() => this.carText.valid)
        )
        .subscribe(() => {
          this.store.dispatch(
            BookingState.submitAboutVehicle({
              chassiNumber: this.carText.value.replace(/\s/g, ""),
              odometer: this.milage.value,
              searchedByRegno: this.formService.searchedByRegno
            })
          );
        })
    );

    this.subscription.add(
      this.milage.valueChanges
        .pipe(
          debounceTime(300),
          filter(() => this.milage.valid)
        )
        .subscribe((mileage) => {
          this.store.dispatch(
            BookingState.updateOdometer({ odometer: mileage })
          );
        })
    );
  }

  get carText(): AbstractControl {
    return this.formService.aboutVehicleForm.get("carText");
  }

  get milage(): AbstractControl {
    return this.formService.aboutVehicleForm.get("milage");
  }

  readonly mileageOutputTransform: OutputTransformFn = (v) =>
    Number(String(v ?? "").replace(/\s/g, ""));

  get validVehicle(): boolean {
    return !!this.carInfo() && !this.error();
  }

  get newBrandUrl(): string {
    return (
      window.location.origin +
      "/" +
      this.carBrandPipe.transform(this.carInfo()) +
      "/form/1/?" +
      "rn=" +
      this.carText.value +
      "&mileage=" +
      this.milage.value
    );
  }

  ngOnDestroy(): void {
    document.getElementById("readpeak")?.remove();
    this.cleanUp();
  }

  private _insertReadPeakScript() {
    const hasReadPeakScript = !!document.getElementById("readpeak");
    if (hasReadPeakScript) return;

    const node = document.createElement("script");
    node.id = "readpeak";
    node.src = "https://static.readpeak.com/js/rpa.js";
    node.async = true;
    document.getElementsByTagName("head")[0].appendChild(node);
  }
}
